import React, { KeyboardEvent, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import Checkbox from "@/components/Core/Checkbox/Checkbox";
import { cn } from "@/utils/cn";

export interface SearchableCheckboxListOption {
	value: string;
	label: string;
}

interface SearchableCheckboxListProps {
	options: SearchableCheckboxListOption[];
	/** Currently checked values. */
	selected?: string[];
	/** Called with a value when its checkbox is toggled (or Enter-selected). */
	onToggle: (value: string) => void;
	/**
	 * Show the search box only once the option count exceeds this. Defaults to 8
	 * so short lists stay clean and only long ones (customers, contracts…) get a
	 * filter. Pass `0` to always show it.
	 */
	searchThreshold?: number;
	/** Raw placeholder text for the search box. Defaults to the `SEARCH` i18n key. */
	searchPlaceholder?: string;
	/** Shown when the search yields no matches. Defaults to the `NO_SEARCH_RESULTS` i18n key. */
	emptyLabel?: string /** Focus the search box on mount. */;
	autoFocus?: boolean;
	/** Read-only: checkboxes render but cannot be toggled. Search stays usable. */
	disabled?: boolean;
	className?: string;
	/** Extra classes for the scrollable list region (e.g. to change `max-h-*`). */
	listClassName?: string;
}

/**
 * Strip diacritics + lowercase so a search for "lonnens" matches "Lönnens" —
 * Swedish customer names are full of å/ä/ö that users rarely type.
 */
const normalize = (s: string) =>
	s
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");

/**
 * A multi-select checkbox list with an optional top search box for quickly
 * narrowing long option sets (hundreds of customers, contracts, …). Generic and
 * domain-agnostic — give it `{ value, label }[]`, the checked `selected` values
 * and an `onToggle`.
 *
 * Inspired by shadcn/ui's Command (cmdk) — search pinned on top, filtered list
 * scrolls below — but kept inline and multi-select instead of a single-select
 * popover, so it drops straight into a filter `Section`.
 *
 * Enter behaviour: when the active search narrows the list to exactly one
 * option, pressing Enter toggles it and clears the query (so you can type the
 * next one). With zero or multiple matches Enter does nothing — the user clicks
 * the checkbox they want. Mirrors the "type to find, Enter when unambiguous" feel.
 */
const SearchableCheckboxList: React.FC<SearchableCheckboxListProps> = ({
	options,
	selected,
	onToggle,
	searchThreshold = 8,
	searchPlaceholder,
	emptyLabel,
	autoFocus,
	disabled,
	className,
	listClassName,
}) => {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");

	const showSearch = options.length > searchThreshold;

	const filtered = useMemo(() => {
		const q = normalize(query.trim());
		if (!q) return options;
		return options.filter((o) => normalize(o.label).includes(q));
	}, [options, query]);

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			// Only auto-select when the filter leaves a single unambiguous match.
			if (filtered.length === 1 && !disabled) {
				onToggle(filtered[0].value);
				setQuery("");
			}
		}
	};

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			{showSearch && (
				<div className="flex h-[2.3125rem] items-center gap-2 rounded-sm border border-solid border-gray-300 bg-white px-2 focus-within:border-blue-900">
					<Search className="h-4 w-4 shrink-0 text-gray-400" />
					<input
						type="text"
						value={query}
						autoFocus={autoFocus}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={searchPlaceholder ?? t("SEARCH" as any)}
						className="w-full border-none bg-transparent text-[0.9375rem] leading-[0.9375rem] text-gray-800 outline-none placeholder:text-gray-400"
					/>
				</div>
			)}

			<div className={cn("flex flex-col gap-2 max-h-48 overflow-auto", listClassName)}>
				{filtered.length === 0 ? (
					<p className="px-1 py-2 text-sm text-gray-400">
						{emptyLabel ?? t("NO_SEARCH_RESULTS" as any)}
					</p>
				) : (
					filtered.map((o) => (
						<Checkbox
							key={o.value}
							customStyle
							labelClassName="text-base"
							label={o.label}
							isChecked={!!selected?.includes(o.value)}
							disabled={disabled}
							onChange={() => onToggle(o.value)}
						/>
					))
				)}
			</div>
		</div>
	);
};

SearchableCheckboxList.displayName = "SearchableCheckboxList";

export default SearchableCheckboxList;
